import { useEffect, useRef, useState } from "react";
import type { City, TripLeg, StopActions, TrainRoute } from "../data/types";
import { trainRouteToTripLeg } from "../data/types";
import { findRoutesLive } from "../services/routeService";
import { durationMinutes, nextDeparture } from "../services/journeyTime";
import { reportError } from "../services/errorService";

function placeholder(from: City, to: City, scheduleKey: string): TripLeg {
  return {
    fromStationId: from.id, toStationId: to.id,
    departureTime: "", arrivalTime: "", duration: "", operator: "",
    routeId: `ghost-${from.id}-${to.id}`, stations: [],
    routeCoords: [[from.latitude, from.longitude], [to.latitude, to.longitude]],
    isGhostLeg: true, ghostLoading: true, ghostScheduleKey: scheduleKey,
  };
}

function upsert(legs: TripLeg[], leg: TripLeg): TripLeg[] {
  const samePair = (other: TripLeg) => other.fromStationId === leg.fromStationId && other.toStationId === leg.toStationId;
  if (legs.some(other => samePair(other) && (!other.isGhostLeg || other.requiresSelection))) return legs;
  return [...legs.filter(other => !samePair(other)), leg];
}

function findLeg(legs: TripLeg[], from: City, to: City): TripLeg | undefined {
  return legs.find(leg => leg.fromStationId === from.id && leg.toStationId === to.id);
}

function prepareConnections(legs: TripLeg[], stops: City[], scheduleKey: string): TripLeg[] {
  let next = legs.filter(leg => !leg.isGhostLeg || stops.some((stop, i) =>
    stop.id === leg.fromStationId && stops[i + 1]?.id === leg.toStationId));
  for (let i = 0; i < stops.length - 1; i++) {
    const existing = findLeg(next, stops[i], stops[i + 1]);
    if (!existing || (existing.isGhostLeg && !existing.requiresSelection && existing.ghostScheduleKey !== scheduleKey)) {
      next = upsert(next, placeholder(stops[i], stops[i + 1], scheduleKey));
    }
  }
  return next.length === legs.length && next.every((leg, i) => leg === legs[i]) ? legs : next;
}

function fastestViableRoute(routes: TrainRoute[], departure: string): TrainRoute | undefined {
  const earliest = Date.parse(departure);
  let best: TrainRoute | undefined;
  for (const route of routes) {
    const departureTime = route.departureInstant ? Date.parse(route.departureInstant) : earliest;
    if (!Number.isFinite(departureTime) || departureTime < earliest) continue;
    if (!best || durationMinutes(route.duration) < durationMinutes(best.duration)) best = route;
  }
  return best;
}

interface GhostCascade {
  stops: City[];
  actions: StopActions;
  initial: TripLeg[];
  date: string;
  scheduleKey: string;
  signal: AbortSignal;
  publish: (leg: TripLeg) => void;
  scheduleRetry: (retryAt: number) => void;
}

type ConnectionResult = { leg: TripLeg } | { retryAt: number } | undefined;

async function loadConnection(cascade: GhostCascade, index: number, previous: TripLeg | undefined): Promise<ConnectionResult> {
  const { stops, actions, date, scheduleKey, signal } = cascade;
  const from = stops[index];
  const to = stops[index + 1];
  const failed = { ...placeholder(from, to, scheduleKey), ghostLoading: false, ghostError: true };
  // A missing arrival is not enough information to book downstream trains.
  if (index > 0 && (!previous?.arrivalTime || previous.ghostError)) return { leg: failed };
  try {
    const departure = nextDeparture(from, previous, actions, date);
    const { routes } = await findRoutesLive(from, to, departure, 5, signal);
    if (signal.aborted) return;
    const route = fastestViableRoute(routes, departure);
    if (!route) return { leg: failed };
    return { leg: { ...trainRouteToTripLeg(route, {
      fromStationId: from.id, toStationId: to.id, routeCoords: route.routeCoords, isGhostLeg: true,
    }), ghostScheduleKey: scheduleKey } };
  } catch (error) {
    if (signal.aborted) return;
    if (error instanceof Error && error.message === "rate_limited") {
      return { retryAt: (error as Error & { retryAt?: number }).retryAt ?? Date.now() + 60_000 };
    }
    reportError(error, { level: "warning", context: { from: from.city, to: to.city } });
    return { leg: failed };
  }
}

function isResolved(leg: TripLeg | undefined, scheduleKey: string): leg is TripLeg {
  return !!leg && (!leg.isGhostLeg || (leg.ghostScheduleKey === scheduleKey && !leg.ghostLoading && !leg.ghostThrottled));
}

async function resolveConnections(cascade: GhostCascade): Promise<void> {
  const { stops, initial, scheduleKey, signal, publish, scheduleRetry } = cascade;
  let previous: TripLeg | undefined;
  for (let i = 0; i < stops.length - 1; i++) {
    if (signal.aborted) return;
    const existing = findLeg(initial, stops[i], stops[i + 1]);
    // A deliberate itinerary edit stays incomplete until a person chooses a train.
    // Its downstream timetable cannot be scheduled before that arrival is known.
    if (existing?.requiresSelection) return;
    if (isResolved(existing, scheduleKey)) {
      previous = existing;
      continue;
    }
    const result = await loadConnection(cascade, i, previous);
    if (!result || signal.aborted) return;
    if ("retryAt" in result) {
      for (let j = i; j < stops.length - 1; j++) {
        publish({ ...placeholder(stops[j], stops[j + 1], scheduleKey), ghostLoading: false, ghostThrottled: true });
      }
      scheduleRetry(result.retryAt);
      return;
    }
    previous = result.leg;
    publish(previous);
  }
}

export function useGhostLegs(
  tripStops: City[], stopActions: StopActions, tripLegs: TripLeg[],
  setTripLegs: React.Dispatch<React.SetStateAction<TripLeg[]>>, travelDate: string,
  itineraryLegs: TripLeg[] = tripLegs,
): void {
  const latest = useRef({ tripStops, stopActions, tripLegs, travelDate });
  latest.current = { tripStops, stopActions, tripLegs, travelDate };
  const [retry, setRetry] = useState(0);
  // Raw choices own cache invalidation. A derived pending/valid transition can
  // resume the cascade without discarding the automatic arrival that caused it.
  const scheduleKey = JSON.stringify([
    travelDate,
    tripStops.map(s => [s.id, s.hafasId, s.latitude, s.longitude, stopActions[s.id]]),
    itineraryLegs.filter(l => !l.isGhostLeg).map(l => [l.fromStationId, l.toStationId, l.routeId,
      l.departureDate, l.departureTime, l.arrivalDate, l.arrivalTime, l.departureInstant, l.arrivalInstant]),
    itineraryLegs.filter(l => l.requiresSelection).map(l => [l.fromStationId, l.toStationId]),
  ]);
  const pendingSignature = JSON.stringify(tripLegs.filter(l => l.requiresSelection).map(l => [l.fromStationId, l.toStationId]));

  useEffect(() => {
    const { tripStops: stops, stopActions: actions, tripLegs: initial, travelDate: date } = latest.current;
    const controller = new AbortController();
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const publish = (leg: TripLeg) => {
      if (!controller.signal.aborted) setTripLegs(prev => controller.signal.aborted ? prev : upsert(prev, leg));
    };
    const retryCascade = () => setRetry(n => n + 1);
    const scheduleRetry = (retryAt: number) => {
      retryTimer = setTimeout(retryCascade, Math.max(1000, retryAt - Date.now()));
    };
    // All affected automatic connections lose their old schedule immediately.
    setTripLegs(prev => prepareConnections(prev, stops, scheduleKey));
    const timer = setTimeout(() => {
      void resolveConnections({ stops, actions, initial, date, scheduleKey,
        signal: controller.signal, publish, scheduleRetry });
    }, 1500);
    return () => {
      controller.abort();
      clearTimeout(timer);
      clearTimeout(retryTimer);
    };
  }, [scheduleKey, pendingSignature, retry, setTripLegs]);
}
